import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

const VaccinationTrackerDoctorView = ({ pediatricianId, patientId }) => {
  const [vaccines, setVaccines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const token = localStorage.getItem("s2stoken");
  const [saving, setSaving] = useState(false);
  const [savingIndex, setSavingIndex] = useState(null);
  const [deletingIndex, setDeletingIndex] = useState(false);
  const navigate = useNavigate();
  dayjs.extend(utc);

  const isEditable = (createdAt) => {
    if (!createdAt) {
      return false;
    }
    console.log(createdAt, 'db date');
    const now = dayjs(); // current local time
    const created = dayjs.utc(createdAt).local(); // ensure it's in local timezone
    console.log(created, 'created dateafter utc to local');
    // Add 2 days to created time
    const editableUntil = created.add(2, "day");

    return now.isBefore(editableUntil); // true = still editable
  };

  const fetchVaccines = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/get_vaccine_tracker_doctor_view?pediatrician_id=${pediatricianId}&patient_id=${patientId}`,
        {
          method: "GET",
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch data");
      }

      console.log("API Response:", data);

      if (data.success) {
        setVaccines(
          data.data.map((vaccine) => ({
            ...vaccine,
            whether_taken: vaccine["Whether Taken"] === "yes",
            date_when_taken: vaccine["Date When Taken"]
              ? formatDate(vaccine["Date When Taken"])
              : "",
            editable: isEditable(vaccine["Created At"]),
          }))
        );
      } else {
        alert(data.message);
      }
    } catch (error) {
      if (error?.message === "Token has expired") {
        localStorage.removeItem("s2stoken");
        navigate("/");
      }
      console.error("Error fetching vaccination data:", error);
      alert("Error fetching vaccination data.");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchVaccines();
  }, [pediatricianId, patientId]);

  const handleCheckboxChange = (index, e) => {
    const updatedVaccines = [...vaccines];
    const selectedVaccine = updatedVaccines[index];
    const isChecked = e.target.checked;
    const selectedAgeRange = selectedVaccine["Age Range"];

    const hasOtherAgeRangeSelected = updatedVaccines.some(
      (v, i) => i !== index && v.checked && v["Age Range"] !== selectedAgeRange
    );

    if (hasOtherAgeRangeSelected) {
      toast.warn("Save or Delete this group before selecting another");
      updatedVaccines[index] = {
        ...selectedVaccine,
        checked: false,
        isDateActive: false,
        isDirty: false,
        isDateAdded: false,
        date_when_taken: "",
      };
      setVaccines(updatedVaccines);
      return; // ❌ Exit without changing anything
    }

    // If checking → apply to all in the same Age Range
    if (isChecked) {
      const currentDate = new Date().toISOString().split("T")[0]; // yyyy-mm-dd
      updatedVaccines.forEach((v, i) => {
        if (
          v["Age Range"] === selectedAgeRange &&
          !v.whether_taken
        ) {
          updatedVaccines[i] = {
            ...v,
            checked: true,
            isDateActive: true,
            isDirty: true,
            isDateAdded: true,
            // date_when_taken: currentDate,
          };
        }
      });
    } else {
      // If unchecking, only update the clicked one
      updatedVaccines[index] = {
        ...selectedVaccine,
        checked: false,
        isDateActive: false,
        isDirty: false,
        isDateAdded: false,
        date_when_taken: "",
      };
    }

    setVaccines(updatedVaccines);
  };

  console.log(vaccines);
  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Helper function to format "When to Take"
  const formatWhenToTake = (whenToTake, ageRange) => {
    if (ageRange === "0-0 Months") {
      return formatDate(whenToTake.split(" To ")[0]); // Display only start date for "0-0 Months"
    }

    if (whenToTake?.includes("To")) {
      const [startDate, endDate] = whenToTake.split(" To ");
      return startDate === endDate
        ? formatDate(startDate) // Only display one date if they are the same
        : `${formatDate(startDate)} To ${formatDate(endDate)}`; // Display both dates if they differ
    } else {
      return formatDate(whenToTake);
    }
  };

  const handleDateChange = (index, event) => {
    let dateValue = event.target.value;

    const updatedVaccines = [...vaccines];
    const selectedVaccine = updatedVaccines[index];
    const selectedAgeRange = selectedVaccine["Age Range"];

    if (!dateValue) {
      updatedVaccines[index] = {
        ...updatedVaccines[index],
        date_when_taken: "",
        isDateAdded: false,
      };
      setVaccines(updatedVaccines);
      return;
    }
    // Split the date value and check the year part
    const dateParts = dateValue.split("-");
    if (dateParts.length > 0 && dateParts[0].length > 4) {
      // If the year part has more than 4 digits, limit it to 4 digits
      dateParts[0] = dateParts[0].slice(0, 4);
    }

    // Join the date back after modification
    dateValue = dateParts.join("-");

    updatedVaccines.forEach((vaccine,i) =>{
      if (vaccine['Age Range'] === selectedAgeRange && !vaccine?.whether_taken && vaccine?.isDateActive) {
        updatedVaccines[i] = {
          ...vaccine,
          date_when_taken: dateValue,
          isDateAdded: true,
        }
      }
    })
    setVaccines(updatedVaccines);
  };

  const handleSaveChanges = async (index) => {
    const currentVaccine = vaccines[index];
    const selectedAgeRange = currentVaccine["Age Range"];

    // Get all vaccines with same Age Range that are checked and have valid date
    const selectedVaccines = vaccines.filter(
      (v) =>
        v["Age Range"] === selectedAgeRange &&
        v.checked &&
        v.date_when_taken &&
        v.isDateAdded
    );

    if (selectedVaccines.length === 0) {
      return toast.warn("Please select at least one valid vaccine with date.");
    }

    const vaccinePayload = selectedVaccines.map((v) => ({
      vaccine: v.Vaccine,
      whether_taken: "yes",
      date_when_taken: v.date_when_taken,
    }));

    setSaving(true);
    setSavingIndex(selectedAgeRange);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/update_vaccine_tracker_by_doctor`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify({
            patient_id: patientId,
            pediatrician_id: pediatricianId,
            vaccines: vaccinePayload,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to update vaccine");
      }

      toast.success("Vaccines saved successfully");
      setSaving(false);
      setSavingIndex(null);

      // Update UI state
      const updatedVaccines = [...vaccines];
      vaccines.forEach((v, i) => {
        if (
          v["Age Range"] === selectedAgeRange &&
          v.checked &&
          v.isDateAdded &&
          !v.whether_taken &&
          v.date_when_taken
        ) {
          updatedVaccines[i] = {
            ...v,
            whether_taken: true,
            date_when_taken: formatDate(v.date_when_taken),
            isDirty: false,
            checked: false,
            isDateActive: false,
            isDateAdded: false,
            editable: true,
          };
        }
      });

      setVaccines(updatedVaccines);
    } catch (error) {
      setSaving(false);
      setSavingIndex(null);
      if (error?.message === "Token has expired") {
        localStorage.removeItem("s2stoken");
        navigate("/");
      }
      console.error("Error saving vaccine:", error);
      toast.error("Failed to save vaccine.");
    }
  };

  async function deleteVaccine(vaccine, index) {
    setDeletingIndex(index);
    try {
      setSaving(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/delete_vaccine_tracker`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify({
            patient_id: patientId,
            doctor_id: pediatricianId,
            vaccine,
          }),
        }
      );

      const data = await response.json();
      console.log(data, "delete response vaccines");
      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to update vaccine");
      }
      toast.success("Vaccine cleared successfully");
      setSaving(false);
      const updatedVaccines = [...vaccines];
      updatedVaccines[index] = {
        ...updatedVaccines[index],
        whether_taken: false,
        date_when_taken: "",
        isDirty: false,
        checked: false,
        isDateActive: false,
        isDateAdded: false,
        editable: false,
      };
      setVaccines(updatedVaccines);
    } catch (error) {
      if (error?.message === "Token has expired") {
        localStorage.removeItem("s2stoken");
        navigate("/");
      }
      console.error("Error saving vaccine:", error);
      toast.error("Failed to save vaccine.");
    }
  }

  if (isLoading) {
    return <p>Loading...</p>;
  }

  return (
    <div className="vaccination-tracker">
      <h1>Vaccination Tracker</h1>
      <div className="legends">
        <p>
          *Only in endemic areas <br />
          **Penta = DPT+HiB+HepatitisB <br />
          ***IAP Recommended (Indian Academy of Pediatricians)
        </p>
      </div>
      <div className="vaccination-tracker-wrapper">
        <table>
          <thead>
            <tr>
              <th>Age Range</th>
              <th>When to Take</th>
              <th>Vaccine</th>
              <th>Whether Taken?</th>
              <th>Date When Taken</th>
            </tr>
          </thead>
          <tbody>
            {vaccines.map((vaccine, index) => (
              <tr key={index}>
                {index === 0 ||
                vaccine["Age Range"] !== vaccines[index - 1]["Age Range"] ? (
                  <td
                    rowSpan={
                      vaccines.filter(
                        (v) => v["Age Range"] === vaccine["Age Range"]
                      ).length
                    }
                  >
                    {vaccine["Age Range"]}
                  </td>
                ) : null}
                {index === 0 ||
                vaccine["When to Take"] !==
                  vaccines[index - 1]["When to Take"] ? (
                  <td
                    rowSpan={
                      vaccines.filter(
                        (v) => v["When to Take"] === vaccine["When to Take"]
                      ).length
                    }
                  >
                    {formatWhenToTake(
                      vaccine["When to Take"],
                      vaccine["Age Range"]
                    )}
                  </td>
                ) : null}
                <td>{vaccine.Vaccine}</td>
                <td>
                  {vaccine.whether_taken ? (
                    <>
                      <span style={{ color: "green" }}>&#10003;</span>
                      {vaccine.editable && (
                        <button
                          disabled={saving}
                          style={{
                            marginLeft: "8px",
                            paddingInline: "2px",
                            paddingBlock: "2px",
                            borderRadius: "5px",
                            backgroundColor: "#e84848",
                            color: "white",
                            cursor: "pointer",
                            border: "none",
                          }}
                          onClick={() => deleteVaccine(vaccine.Vaccine, index)}
                        >
                          {saving && deletingIndex === index
                            ? "changing"
                            : "change"}
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        type="checkbox"
                        checked={vaccine.checked}
                        onChange={(e) => handleCheckboxChange(index, e)}
                      />
                    </>
                  )}
                </td>
                <td>
                  {!vaccine.isDirty ? (
                    <span>{vaccine.date_when_taken}</span>
                  ) : (
                    <>
                      {vaccine.isChecked ? (
                        <span>{vaccine.date_when_taken}</span>
                      ) : (
                        <>
                          <div className="dateSave-container">
                            <input
                              className="date-input"
                              type="date"
                              value={vaccine.date_when_taken || null}
                              onChange={(event) =>
                                handleDateChange(index, event)
                              }
                              max={new Date().toISOString().split("T")[0]} // This disables future dates
                              disabled={!vaccine.isDateActive} // Disable date input if isDateActive is false
                            />
                            {vaccine.isDirty && (
                              <button
                                className="save-button"
                                onClick={() => handleSaveChanges(index)}
                                disabled={saving}
                              >
                                {saving && savingIndex === vaccine["Age Range"]
                                  ? "Saving..."
                                  : "Save"}
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VaccinationTrackerDoctorView;